import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import { Button, Chip, Divider, Grid, Icon, Typography } from "@mui/material";
import useMemberStore from "features/member/store/memberStore";
import { GetPendingMembers } from "features/member/api_1/hooks";
import { CheckCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import useAuth from "hooks/useAuth";
import { useEffect, useMemo, useState } from "react";
import { IMemberGridModel, ISelectMember } from "features/member/api_1/types";
import StyledDataGrid from "components/@extended/StyledDataGrid";
import { showError, showSuccess } from "utils/showMessages";
import { useQueryClient } from "@tanstack/react-query";
import { useDialog } from "contexts/ToolsProvider/DialogContext";
import { ApproveAllMembers } from "features/member/api_1/mutation";
import { member } from "features/member/api_1/query";
import MainCard from "components/MainCard";
import { ApiResponse } from "common/models/baseModel";
// import { ApiResponse } from "common/models/ApiResponse";

const PendingMembers = () => {
  // const navigateTo = useNavigate();
  // const { modules } = useAuth();
  const showDialog = useDialog();
  const columns: GridColDef<IMemberGridModel>[] = useMemo(
    () => [
      {
        field: "memberName",
        headerName: "Name",
        filterable: false,
        flex: 1.5,
      },
      {
        field: "enrollmentNo",
        headerName: "Enrollment No",
        filterable: false,
        flex: 1,
      },
      {
        field: "instituteName",
        headerName: "Institute Name",
        filterable: false,
        flex: 1,
      },
      {
        field: "programName",
        headerName: "Program Name",
        filterable: false,
        flex: 2.2,
      },
      {
        // field: "year",
        field: "batch",
        headerName: "Graduation  Year",
        flex: 0.8,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "memberStatus",
        headerName: "Member Status",
        filterable: false,
        sortable: false,
        flex: 0.8,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <>
            <>
              {params?.value == 1 ? (
                <>
                  <Chip
                    sx={{ borderRadius: 10 }}
                    label="Pending"
                    size="small"
                    color="warning"
                    variant="light"
                  />
                </>
              ) : (
                <>
                  {params.value == 2 ? (
                    <>
                      <Chip
                        sx={{ borderRadius: 10 }}
                        label="Approved"
                        size="small"
                        color="success"
                        variant="light"
                      />
                    </>
                  ) : (
                    <>
                      {params.value == 3 ? (
                        <>
                          <Chip
                            sx={{ borderRadius: 10 }}
                            label="Rejected"
                            size="small"
                            color="error"
                            variant="light"
                          />
                        </>
                      ) : (
                        <></>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          </>
        ),
      },
    ],
    []
  );

  const { postModel, handleSorting, handlePagination } = useMemberStore();
  const { data, isFetching } = GetPendingMembers(postModel);
  const [selectedRows, setSelectedRows] = useState<IMemberGridModel[]>([]);
  const [memberID, setMemberID] = useState<number[]>([]);

  const handleRowSelectionChange = (
    newSelectionModel: GridRowSelectionModel
  ) => {
    const newSelectedRows = newSelectionModel
      .map((id) => {
        return data?.apiData?.data?.find((row) => row.memberID === id);
      })
      .filter((row) => row !== undefined) as IMemberGridModel[];

    setSelectedRows((prevSelectedRows) => {
      const updatedSelectedRows = prevSelectedRows.filter((row) =>
        newSelectionModel.includes(row.memberID)
      );
      newSelectedRows.forEach((row) => {
        if (
          !updatedSelectedRows.some(
            (selectedRow) => selectedRow.memberID === row.memberID
          )
        ) {
          updatedSelectedRows.push(row);
        }
      });
      return updatedSelectedRows;
    });

    setMemberID(newSelectionModel as number[]);
  };

  useEffect(() => {
    const memberids = selectedRows.map((obj) => obj.memberID);
    setMemberID(memberids);
  }, [selectedRows]);

  // useEffect(() => {
  //   if (postModel.filterModel) {
  //     postModel.filterModel.memberStatus = 1;
  //     reset(postModel.filterModel);
  //   }
  // }, [postModel.filterModel]);

  const queryClient = useQueryClient();
  const { mutate: approveallMembers } = ApproveAllMembers();

  const handleApproveResponse = async (
    response: ApiResponse<number> | undefined
  ) => {
    if (response?.status === 200) {
      const responseResult = await response?.apiData;

      if (responseResult == 1) {
        showSuccess(memberID.length + ` Members are Approved successfully`);
        queryClient.invalidateQueries({
          queryKey: member.pendingMembers(postModel).queryKey,
        });
        setSelectedRows([]);
        setMemberID([]);
      } else {
        showError("failed Approve");
      }
    }
  };

  const handleApproveAllMembers = async () => {
    const confirmed = await showDialog({
      title: `Confirm Approval for Alumni Members`,
      message: (
        <span>
          You have selected "<strong>{memberID.length}</strong>" alumni members.
          Are you sure you want to approve them?
        </span>
      ),
      icon: <CheckCircleOutlined />,
      button: "Approve",
      color: "success",
      iconColor: "#63c930",
      iconBackgroundColor: "#f6ffed",
    });

    if (confirmed) {
      if (memberID.length === 0) {
        showError("No members selected for approval.");
        return;
      }
      const selectedIds: ISelectMember = { memberID };
      approveallMembers(selectedIds, {
        onSettled(response) {
          handleApproveResponse(response);
        },
      });
    }
  };

  return (
    <>
      <MainCard sx={{ my: 1, mt: 2 }}>
        <Typography variant="h3">
          <Icon sx={{ marginRight: "5px" }}>
            <SearchOutlined />
          </Icon>
          Pending Member Approvals
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Grid container direction={"row"} spacing={2}>
          <Grid item md={12}>
            <Grid container>
              <Grid item xs={2} sx={{ mb: 2 }} alignContent={"center"}>
                {selectedRows.length > 0 && (
                  <Chip
                    label={`${memberID.length} member(s) selected`}
                    color="secondary"
                    variant="combined"
                    size="small"
                  />
                )}
              </Grid>

              <Grid item xs={10} sx={{ textAlign: "right", mb: 2 }}>
                {selectedRows.length > 0 && (
                  <Button
                    variant="contained"
                    size="medium"
                    color="success"
                    sx={{ marginX: 1 }}
                    onClick={handleApproveAllMembers}
                    startIcon={<CheckCircleOutlined />}
                  >
                    Approve
                  </Button>
                )}
              </Grid>
            </Grid>

            <Grid container sx={{ height: 600, width: "100%" }}>
              <StyledDataGrid
                checkboxSelection
                onRowSelectionModelChange={handleRowSelectionChange}
                getRowId={(row) => row.memberID}
                rowSelectionModel={selectedRows.map((row) => row.memberID)}
                isRowSelectable={(params) => params.row.memberStatus === 1}
                rows={data ? data?.apiData?.data : []}
                keepNonExistentRowsSelected
                columns={columns}
                sx={{
                  "& .MuiDataGrid-columnHeaderTitle": {
                    whiteSpace: "wrap",
                    fontWeight: 600,
                    lineHeight: "normal",
                    fontSize: "13px",
                  },
                  "& .MuiDataGrid-cell": {
                    whitespace: "wrap",
                    fontSize: 14,
                  },
                }}
                density="compact"
                disableRowSelectionOnClick
                pageSizeOptions={[5, 10, 25, 50, 100]}
                paginationMode="server"
                sortingMode="server"
                rowCount={data?.apiData?.count}
                initialState={{
                  pagination: {
                    paginationModel: { ...postModel.pagingModel },
                  },
                }}
                sortModel={postModel.sortModel}
                onSortModelChange={handleSorting}
                onPaginationModelChange={handlePagination}
                disableColumnFilter
                loading={isFetching}
              />
            </Grid>
          </Grid>
        </Grid>
      </MainCard>
    </>
  );
};

export default PendingMembers;
